import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Clock, DollarSign, Filter, ChevronDown, Search,
  Target, BarChart3, Users, Zap, Globe, Code, Settings2,
  CheckCircle, Star
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  { value: "all", label: "All Tasks", icon: Filter },
  { value: "customer_growth", label: "Customer Growth", icon: BarChart3 },
  { value: "hiring", label: "Hiring", icon: Users },
  { value: "automation", label: "Automation", icon: Zap },
  { value: "local_business", label: "Local Business", icon: Globe },
  { value: "web_digital", label: "Web & Digital", icon: Code },
  { value: "operations", label: "Operations", icon: Settings2 },
  { value: "branding", label: "Branding", icon: Star },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "payout_high", label: "Highest payout" },
  { value: "payout_low", label: "Lowest payout" },
  { value: "deadline", label: "Shortest deadline" },
];

export default function DoerFeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [catOpen, setCatOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [claimingId, setClaimingId] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy };
      if (category !== "all") params.category = category;
      const res = await api.get("/api/workspace/open-tasks", { params });
      setTasks(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [category, sortBy]);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user) fetchTasks();
  }, [user, fetchTasks]);

  const handleClaim = async (taskId) => {
    if (!user) { navigate("/login"); return; }
    setClaimingId(taskId);
    try { await api.post(`/api/workspace/tasks/${taskId}/claim`); navigate(`/doer/workspace/${taskId}`); }
    catch (e) { alert(e.response?.data?.detail || "Failed to claim task"); }
    finally { setClaimingId(null); }
  };

  const selectedCat = CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  const selectedSort = SORT_OPTIONS.find(s => s.value === sortBy) || SORT_OPTIONS[0];

  if (user === undefined) return (
    <div style={{ paddingTop: 120, textAlign: "center" }}>
      <div style={{ width: 40, height: 40, border: "3px solid rgba(0,92,238,0.08)", borderTop: "3px solid var(--primary)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ paddingTop: 68, minHeight: "100vh" }}>
      <section style={{ position: "relative", padding: "80px 24px 40px", overflow: "hidden" }}>
        <div className="bg-blob" style={{ width: 500, height: 500, background: "rgba(0,92,238,0.03)", top: "-10%", right: "0%" }} />
        <div className="bg-blob" style={{ width: 350, height: 350, background: "rgba(138,79,255,0.02)", bottom: "0%", left: "10%", animationDelay: "2s" }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
              <Target size={14} color="var(--primary)" />
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 600, color: "var(--primary)", letterSpacing: "0.15em", textTransform: "uppercase" }}>Task Feed</span>
            </div>
            <h1 data-testid="doer-feed-title" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "clamp(2rem, 5vw, 2.8rem)", fontWeight: 300, color: "var(--text-main)", lineHeight: 1.1, marginBottom: 12, letterSpacing: "-0.04em" }}>
              Find tasks. <span style={{ fontWeight: 700 }}>Execute. Earn.</span>
            </h1>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.92rem", color: "var(--text-secondary)", maxWidth: 500, lineHeight: 1.6 }}>Tasks matched to your skills. Accept, execute, and earn on delivery.</p>
            <div style={{ display: "flex", gap: 20, marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--primary)" }} />
                <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}><strong style={{ color: "var(--text-main)" }}>{tasks.length}</strong> open tasks</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <CheckCircle size={13} color="var(--primary)" />
                <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Matched to your profile</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Category filter */}
          <div style={{ position: "relative" }}>
            <button data-testid="feed-category-filter" onClick={() => { setCatOpen(!catOpen); setSortOpen(false); }} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "0.45rem 0.9rem", borderRadius: 10,
              background: "var(--bg-elevated)", border: "1px solid var(--bg-neutral)",
              cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 500,
              color: "var(--text-main)", transition: "all 200ms",
            }}>
              {React.createElement(selectedCat.icon, { size: 13, color: "var(--primary)", strokeWidth: 1.5 })}
              {selectedCat.label}
              <ChevronDown size={11} style={{ color: "var(--text-muted)", transform: catOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            </button>
            {catOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 210, zIndex: 50, background: "var(--bg-elevated)", border: "1px solid var(--glass-border)", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.06)", padding: "4px" }}>
                {CATEGORIES.map(cat => (
                  <button key={cat.value} data-testid={`feed-cat-${cat.value}`} onClick={() => { setCategory(cat.value); setCatOpen(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "0.5rem 0.8rem", borderRadius: 8, background: category === cat.value ? "var(--accent-subtle)" : "transparent", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: category === cat.value ? "var(--accent-text)" : "var(--text-main)", fontWeight: category === cat.value ? 600 : 400, textAlign: "left" }}>
                    {React.createElement(cat.icon, { size: 13, strokeWidth: 1.5 })} {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <button data-testid="feed-sort-filter" onClick={() => { setSortOpen(!sortOpen); setCatOpen(false); }} style={{
              display: "flex", alignItems: "center", gap: 7, padding: "0.45rem 0.9rem", borderRadius: 10,
              background: "var(--bg-elevated)", border: "1px solid var(--bg-neutral)",
              cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 500,
              color: "var(--text-main)", transition: "all 200ms",
            }}>
              {selectedSort.label}
              <ChevronDown size={11} style={{ color: "var(--text-muted)", transform: sortOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            </button>
            {sortOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 180, zIndex: 50, background: "var(--bg-elevated)", border: "1px solid var(--glass-border)", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.06)", padding: "4px" }}>
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.value} data-testid={`feed-sort-${opt.value}`} onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    style={{ display: "block", width: "100%", padding: "0.5rem 0.8rem", borderRadius: 8, background: sortBy === opt.value ? "var(--accent-subtle)" : "transparent", border: "none", cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: sortBy === opt.value ? "var(--accent-text)" : "var(--text-main)", fontWeight: sortBy === opt.value ? 600 : 400, textAlign: "left" }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 40, height: 40, border: "3px solid rgba(0,92,238,0.08)", borderTop: "3px solid var(--primary)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
          </div>
        ) : tasks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Search size={36} color="var(--bg-neutral)" strokeWidth={1.5} style={{ marginBottom: 12 }} />
            <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", fontSize: "0.92rem" }}>No open tasks right now. Check back soon.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
            {tasks.map((task, i) => (
              <motion.div key={task.id} data-testid={`feed-task-card-${i}`}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }}
                className="glass-card" style={{ padding: "1.6rem", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>{task.category?.replace(/_/g, " ")}</span>
                  {task.is_matched && (
                    <span data-testid={`match-badge-${i}`} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 100, background: "var(--accent-subtle)", border: "1px solid rgba(0,92,238,0.12)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", fontWeight: 600, color: "var(--primary)" }}>
                      <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--primary)" }} /> Matched
                    </span>
                  )}
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.05rem", fontWeight: 600, color: "var(--text-main)", lineHeight: 1.3, marginBottom: 4 }}>{task.interpreted_goal || task.original_query}</h3>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{task.original_query}</p>
                </div>
                {task.tags?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {task.tags.slice(0, 4).map(tag => (
                      <span key={tag} style={{ padding: "2px 9px", borderRadius: 100, background: "var(--bg-soft)", color: "var(--text-secondary)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", fontWeight: 500 }}>{tag}</span>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <DollarSign size={12} color="var(--primary)" strokeWidth={1.5} />
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--text-main)" }}>${task.estimated_payout?.toLocaleString()}</span>
                    <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)" }}>est. payout</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock size={11} color="var(--text-muted)" strokeWidth={1.5} />
                    <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.75rem", color: "var(--text-muted)" }}>{task.timeframe_min}\u2013{task.timeframe_max} days</span>
                  </div>
                </div>
                <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid var(--glass-border)" }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)" }}>by <strong style={{ color: "var(--text-secondary)" }}>{task.poster_name}</strong></span>
                  <button data-testid={`accept-task-${i}`} onClick={() => handleClaim(task.id)} disabled={claimingId === task.id} className="btn-primary"
                    style={{ padding: "0.45rem 1.2rem", fontSize: "0.82rem", opacity: claimingId === task.id ? 0.5 : 1, display: "flex", alignItems: "center", gap: 6 }}>
                    {claimingId === task.id ? "Accepting..." : "Accept"} <ArrowRight size={13} strokeWidth={1.5} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {!user && (
        <section style={{ padding: "50px 24px 70px" }}>
          <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.4rem", fontWeight: 300, marginBottom: 10, letterSpacing: "-0.03em" }}>
              Ready to <span style={{ fontWeight: 700 }}>start executing</span>?
            </h2>
            <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", marginBottom: 20, fontSize: "0.88rem" }}>Create a free account to accept tasks and earn.</p>
            <button data-testid="feed-cta-register" onClick={() => navigate("/register")} className="btn-primary" style={{ padding: "0.65rem 2rem" }}>Get started</button>
          </div>
        </section>
      )}
    </div>
  );
}
